// src/app/core/services/api-client.service.ts

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, retry, finalize, timeout, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoadingService } from './loading.service';

export interface ApiResponse<T = any> {
  data?: T;
  success: boolean;
  message?: string;
  error?: string;
  status: number;
  Status: boolean;
  meta?: {
    total?: number;
    page?: number;
    perPage?: number;
    hasMore?: boolean;
  };
}

export interface ApiRequestConfig {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams | { [param: string]: string | number | boolean | readonly (string | number | boolean)[] };
  timeout?: number;
  retries?: number;
  withCredentials?: boolean;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
  showLoading?: boolean;
  skipInterceptors?: boolean;
  baseUrl?: string;
}

export interface PaginatedRequest {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  filters?: Record<string, any>;
}

export interface RequestCacheConfig {
  enabled: boolean;
  ttl?: number; // Time to live in milliseconds
  key?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly loadingService = inject(LoadingService, { optional: true });

  // Base URLs for different environments
  private readonly baseUrls = {
    cas: environment.CAS_BASE_RUL,
    main: environment.BASE_API,
    aqsatel: environment.AQSATEL_API,
    mingle: environment.Prepaid_Mingle_API,
    unity: environment.UNITY_BUYFLOW,
    mingleBuyflow: environment.MINGLE_BUYFLOW
  };

  // Default configuration
  private readonly defaultConfig: ApiRequestConfig = {
    timeout: 300000, // 5 minutes
    retries: 0, // Disabled automatic retry - APIs should handle retries explicitly if needed
    showLoading: false,
    responseType: 'json'
  };

  // Request cache for GET requests
  private requestCache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  // Active requests to prevent duplicate API calls
  private activeRequests = new Map<string, Observable<any>>();

  // Loading state subject
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor() {
    // Clear cache every 5 minutes
    setInterval(() => this.clearExpiredCache(), 5 * 60 * 1000);
  }

  /**
   * GET request
   */
  get<T = any>(
    endpoint: string,
    config: ApiRequestConfig & { cache?: RequestCacheConfig } = {}
  ): Observable<ApiResponse<T>> {
    const fullUrl = this.buildUrl(endpoint, config.baseUrl);
    const requestKey = this.buildCacheKey('GET', fullUrl, config.params);

    // Check cache first if enabled
    if (config.cache?.enabled) {
      const cachedData = this.getFromCache(requestKey, config.cache.ttl);
      if (cachedData) {
        return new Observable(subscriber => {
          subscriber.next(cachedData);
          subscriber.complete();
        });
      }
    }

    // Check if same request is already in progress — return the shared observable
    // so all callers share the single in-flight HTTP request (no duplicate calls).
    if (this.activeRequests.has(requestKey)) {
      return this.activeRequests.get(requestKey)!;
    }

    // shareReplay({ bufferSize: 1, refCount: true }) makes this a hot observable:
    //   - All concurrent subscribers share one HTTP request
    //   - finalize (before shareReplay) cleans up the map entry once the request
    //     completes or errors; refCount:true tears down the source when no
    //     subscribers remain
    const request$ = this.executeRequest<T>('GET', fullUrl, null, config).pipe(
      tap(response => {
        if (config.cache?.enabled) {
          this.setCache(requestKey, response, config.cache.ttl);
        }
      }),
      finalize(() => {
        this.activeRequests.delete(requestKey);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.activeRequests.set(requestKey, request$);

    return request$;
  }

  /**
   * POST request
   */
  post<T = any>(
    endpoint: string,
    body: any,
    config: ApiRequestConfig = {}
  ): Observable<ApiResponse<T>> {
    const fullUrl = this.buildUrl(endpoint, config.baseUrl);
    return this.executeRequest<T>('POST', fullUrl, body, config);
  }

  /**
   * PUT request
   */
  put<T = any>(
    endpoint: string,
    body: any,
    config: ApiRequestConfig = {}
  ): Observable<ApiResponse<T>> {
    const fullUrl = this.buildUrl(endpoint, config.baseUrl);
    return this.executeRequest<T>('PUT', fullUrl, body, config);
  }

  /**
   * PATCH request
   */
  patch<T = any>(
    endpoint: string,
    body: any,
    config: ApiRequestConfig = {}
  ): Observable<ApiResponse<T>> {
    const fullUrl = this.buildUrl(endpoint, config.baseUrl);
    return this.executeRequest<T>('PATCH', fullUrl, body, config);
  }

  /**
   * DELETE request
   */
  delete<T = any>(
    endpoint: string,
    config: ApiRequestConfig = {}
  ): Observable<ApiResponse<T>> {
    const fullUrl = this.buildUrl(endpoint, config.baseUrl);
    return this.executeRequest<T>('DELETE', fullUrl, null, config);
  }

  /**
   * Upload file(s)
   */
  upload<T = any>(
    endpoint: string,
    formData?: FormData,
    config: ApiRequestConfig = {}
  ): Observable<ApiResponse<T>> {
    const fullUrl = this.buildUrl(endpoint, config.baseUrl);
    const mergedConfig = { ...this.defaultConfig, ...config };

    // Use auth + default headers but remove Content-Type so FormData sets boundary
    let headers = this.buildHeaders(mergedConfig);
    headers = headers.delete('Content-Type');

    const params = this.buildParams(mergedConfig.params);

    // Show loading if enabled - using task-based approach
    const taskId = `api-upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    if (mergedConfig.showLoading) {
      this.setLoading(true);
      if (this.loadingService) {
        this.loadingService.show(taskId);
      }
    }

    return this.http.post<ApiResponse<T>>(fullUrl, formData, {
      headers,
      params,
      withCredentials: mergedConfig.withCredentials,
      responseType: 'json'
    }).pipe(
      retry(mergedConfig.retries || 0),
      catchError(error => this.handleError(error)),
      finalize(() => {
        if (mergedConfig.showLoading) {
          this.setLoading(false);
          if (this.loadingService) {
            this.loadingService.hide(taskId);
          }
        }
      })
    );
  }

  /**
   * Download file
   */
  download(
    endpoint: string,
    filename?: string,
    config: ApiRequestConfig = {}
  ): Observable<Blob> {
    const fullUrl = this.buildUrl(endpoint, config.baseUrl);
    const downloadConfig = {
      ...config,
      responseType: 'blob' as const,
      showLoading: true
    };

    // Generate unique task ID for this request
    const taskId = `api-download-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Show loading using task-based approach
    if (downloadConfig.showLoading && this.loadingService) {
      this.loadingService.show(taskId);
    }

    return this.http.get(fullUrl, {
      headers: this.buildHeaders(downloadConfig),
      params: this.buildParams(downloadConfig.params),
      responseType: 'blob',
      withCredentials: downloadConfig.withCredentials
    }).pipe(
      catchError(error => this.handleError(error)),
      finalize(() => {
        if (downloadConfig.showLoading && this.loadingService) {
          this.loadingService.hide(taskId);
        }
      })
    );
  }

  /**
   * Paginated GET request
   */
  getPaginated<T = any>(
    endpoint: string,
    paginationConfig: PaginatedRequest = {},
    config: ApiRequestConfig = {}
  ): Observable<ApiResponse<T[]>> {
    const params = this.buildPaginationParams(paginationConfig);
    const requestConfig = {
      ...config,
      params: { ...params, ...(config.params || {}) }
    };

    return this.get<T[]>(endpoint, requestConfig);
  }

  /**
   * Batch requests - execute multiple requests in parallel
   */
  batch(requests: Array<{
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    endpoint: string;
    body?: any;
    config?: ApiRequestConfig;
  }>): Observable<ApiResponse<any>[]> {
    const batchRequests = requests.map(request => {
      switch (request.method) {
        case 'GET':
          return this.get(request.endpoint, request.config);
        case 'POST':
          return this.post(request.endpoint, request.body, request.config);
        case 'PUT':
          return this.put(request.endpoint, request.body, request.config);
        case 'PATCH':
          return this.patch(request.endpoint, request.body, request.config);
        case 'DELETE':
          return this.delete(request.endpoint, request.config);
        default:
          throw new Error(`Unsupported method: ${request.method}`);
      }
    });

    return new Observable(subscriber => {
      Promise.all(batchRequests.map(req => req.toPromise()))
        .then(results => {
          subscriber.next(results as ApiResponse<any>[]);
          subscriber.complete();
        })
        .catch(error => {
          subscriber.error(error);
        });
    });
  }

  /**
   * Health check endpoint
   */
  healthCheck(baseUrl?: string): Observable<ApiResponse<any>> {
    return this.get('health', {
      baseUrl,
      cache: { enabled: true, ttl: 30000 } // Cache for 30 seconds
    });
  }

  /**
   * Clear all cached requests
   */
  clearCache(): void {
    this.requestCache.clear();
  }

  /**
   * Clear cache for specific endpoint
   */
  clearCacheForEndpoint(endpoint: string): void {
    const keysToDelete: string[] = [];
    this.requestCache.forEach((value, key) => {
      if (key.includes(endpoint)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.requestCache.delete(key));
  }

  /**
   * Get API base URL for specific service
   */
  getBaseUrl(service: keyof typeof this.baseUrls): string {
    return this.baseUrls[service] || this.baseUrls.main;
  }

  getCurrentCompanyBaseUrl(): string {
    // DYNAMIC: Get base URL from localStorage (set during login for current company)
    let dynamicBaseUrl = localStorage.getItem('BaseUrl');

    return dynamicBaseUrl
  }

  /**
   * Private methods
   */
  private executeRequest<T>(
    method: string,
    url: string,
    body: any,
    config: ApiRequestConfig
  ): Observable<ApiResponse<T>> {
    const mergedConfig = { ...this.defaultConfig, ...config };

    // Extract array bracket params (e.g. 'planAreas[]') and embed them directly
    // in the URL to prevent Angular's HttpParams from encoding [ ] as %5B %5D
    let finalUrl = url;
    if (mergedConfig.params && !(mergedConfig.params instanceof HttpParams)) {
      const rawParts: string[] = [];
      const cleanParams: any = {};
      Object.keys(mergedConfig.params as object).forEach(key => {
        const value = (mergedConfig.params as any)[key];
        if (Array.isArray(value) && key.endsWith('[]')) {
          (value as any[]).forEach(v => rawParts.push(`${key}=${encodeURIComponent(v)}`));
        } else {
          cleanParams[key] = value;
        }
      });
      if (rawParts.length > 0) {
        finalUrl = finalUrl + (finalUrl.includes('?') ? '&' : '?') + rawParts.join('&');
        mergedConfig.params = cleanParams;
      }
    }

    const headers = this.buildHeaders(mergedConfig);
    const params = this.buildParams(mergedConfig.params);

    // Generate unique task ID for this request
    const taskId = `api-request-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Show loading if enabled - using task-based approach
    if (mergedConfig.showLoading) {
      this.setLoading(true);
      if (this.loadingService) {
        this.loadingService.show(taskId);
      }
    }

    const httpOptions: any = {
      headers,
      params,
      withCredentials: mergedConfig.withCredentials,
      responseType: mergedConfig.responseType
    };

    let request$: Observable<any>;

    switch (method.toLowerCase()) {
      case 'get':
        request$ = this.http.get(finalUrl, httpOptions);
        break;
      case 'post':
        request$ = this.http.post(finalUrl, body, httpOptions);
        break;
      case 'put':
        request$ = this.http.put(finalUrl, body, httpOptions);
        break;
      case 'patch':
        request$ = this.http.patch(finalUrl, body, httpOptions);
        break;
      case 'delete':
        request$ = this.http.delete(finalUrl, httpOptions);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return request$.pipe(
      timeout(mergedConfig.timeout || 30000),
      retry(mergedConfig.retries || 0),
      catchError(error => this.handleError(error)),
      finalize(() => {
        if (mergedConfig.showLoading) {
          this.setLoading(false);
          if (this.loadingService) {
            this.loadingService.hide(taskId);
          }
        }
      })
    );
  }

  private buildUrl(endpoint: string, baseUrl?: string): string {
    // If endpoint is already a full URL, return as is
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return endpoint;
    }

    // Use provided baseUrl or determine based on current company
    let base = baseUrl;
    if (!base) {
      const currentCompany = localStorage.getItem('currentCompany') || 'unity';
      base = this.getCurrentCompanyBaseUrl();

      // Enhanced logging to track base URL changes
      // console.log('🔍 API URL Building:', {
      //   endpoint,
      //   currentCompany,
      //   selectedBaseUrl: base,
      //   availableBaseUrls: {
      //     unity: this.baseUrls.main,
      //     mingle: this.baseUrls.mingle
      //   }
      // });
    }

    // If base is null (e.g., during logout when localStorage is cleared), use default
    if (!base) {
      console.warn('⚠️ Base URL is null, using default unity base URL');
      base = this.baseUrls.main;
    }

    // Fix malformed protocols (https/ or http/ with single slash)
    if (base.startsWith('https/') && !base.startsWith('https://')) {
      base = base.replace('https/', 'https://');
      console.log('🔧 Fixed malformed https protocol:', base);
    }
    if (base.startsWith('http/') && !base.startsWith('http://')) {
      base = base.replace('http/', 'http://');
      console.log('🔧 Fixed malformed http protocol:', base);
    }

    // CRITICAL FIX: Check if base URL is already an absolute URL
    if (base.startsWith('http://') || base.startsWith('https://')) {
      // Base is absolute URL - use it directly
      const normalizedBase = base.replace(/\/+$/, '');
      const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const finalUrl = `${normalizedBase}${normalizedEndpoint}`;
      // console.log('🌐 Final API URL (absolute base):', finalUrl);
      return finalUrl;
    }

    // Base might be a domain without protocol (e.g., 'prepaid-api.aqsatel.com')
    // Check if it looks like a domain (contains a dot and no slashes at the start)
    if (base.includes('.') && !base.startsWith('/')) {
      // It's a domain without protocol - add https://
      const normalizedBase = `https://${base}`.replace(/\/+$/, '');
      const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const finalUrl = `${normalizedBase}${normalizedEndpoint}`;
      console.log('🌐 Final API URL (domain without protocol):', finalUrl);
      return finalUrl;
    }

    // Base is relative path - construct from current origin
    const normalizedBase = base.replace(/^\/+/, '').replace(/\/+$/, '');
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const finalUrl = normalizedBase
      ? `${normalizedBase}${normalizedEndpoint}`
      : `${normalizedEndpoint}`;

    // console.log('🌐 Final API URL (relative base):', finalUrl);
    return finalUrl;
  }

  private buildHeaders(config: ApiRequestConfig): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'origin': 'https://demo.aqsatel.com',
      'referer': 'https://demo.aqsatel.com/'
    });

    // Add custom headers
    if (config.headers) {
      if (config.headers instanceof HttpHeaders) {
        headers = config.headers;
      } else {
        Object.keys(config.headers).forEach(key => {
          headers = headers.set(key, config.headers![key] as string);
        });
      }
    }

    return headers;
  }

  private buildParams(params?: any): HttpParams {
    let httpParams = new HttpParams();

    if (params) {
      if (params instanceof HttpParams) {
        httpParams = params;
      } else {
        Object.keys(params).forEach(key => {
          const value = params[key];
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              // If the key ends with [], send multiple parameters with bracket notation
              if (key.endsWith('[]')) {
                const baseKey = key.slice(0, -2); // Remove the [] part
                value.forEach(v => httpParams = httpParams.append(`${baseKey}[]`, v.toString()));
              } else {
                // For regular arrays, join with commas
                httpParams = httpParams.set(key, value.join(','));
              }
            } else {
              httpParams = httpParams.set(key, value.toString());
            }
          }
        });
      }
    }

    return httpParams;
  }

  private buildPaginationParams(config: PaginatedRequest): Record<string, any> {
    const params: Record<string, any> = {};

    if (config.page !== undefined) params['page'] = config.page;
    if (config.perPage !== undefined) params['perPage'] = config.perPage;
    if (config.sortBy) params['sortBy'] = config.sortBy;
    if (config.sortOrder) params['sortOrder'] = config.sortOrder;
    if (config.search) params['search'] = config.search;
    if (config.filters) {
      Object.keys(config.filters).forEach(key => {
        params[key] = config.filters![key];
      });
    }

    return params;
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'An unexpected error occurred';
    let errorCode = 'UNKNOWN_ERROR';

    // Handle RxJS TimeoutError (request took longer than the configured timeout)
    if (error?.name === 'TimeoutError') {
      const timeoutMs = this.defaultConfig.timeout || 30000;
      errorMessage = `Request timed out after ${timeoutMs / 1000}s. Please try again.`;
      errorCode = 'REQUEST_TIMEOUT';
      const apiError: ApiResponse = {
        success: false, error: errorMessage, status: 408,
        message: errorMessage, Status: false
      };
      return throwError(() => apiError);
    }

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
      errorCode = 'CLIENT_ERROR';
    } else {
      // When responseType is 'text', error.error may be a raw JSON string — parse it
      if (typeof error.error === 'string' && error.error) {
        try {
          error.error = JSON.parse(error.error);
        } catch { /* not JSON, leave as-is */ }
      }

      // Server-side error
      errorMessage = error.error?.message || error.message || `HTTP ${error.status}: ${error.statusText}`;
      errorCode = error.error?.code || `HTTP_${error.status}`;

      // Handle specific HTTP status codes
      switch (error.status) {
        case 401:
          errorMessage = 'Unauthorized access. Please login again.';
          errorCode = 'UNAUTHORIZED';
          break;
        case 403:
          errorMessage = 'Access forbidden. You don\'t have permission to perform this action.';
          errorCode = 'FORBIDDEN';
          break;
        case 404:
          errorMessage = 'Resource not found.';
          errorCode = 'NOT_FOUND';
          break;
        case 422: {
          // Try to extract the first specific validation message from the API body
          // e.g. { error: { CustomerId: ["The customer id field must be an integer."] } }
          const validationErrors = error.error?.error;
          if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
            const firstField = Object.keys(validationErrors)[0];
            if (firstField) {
              const msgs = validationErrors[firstField];
              errorMessage = Array.isArray(msgs) && msgs.length > 0
                ? msgs[0]
                : (error.error?.message || 'Validation failed. Please check your input.');
            } else {
              errorMessage = error.error?.message || 'Validation failed. Please check your input.';
            }
          } else if (typeof validationErrors === 'string' && validationErrors) {
            // error.error.error is a plain string like "Already a plan change in progress..."
            // Use the specific error string directly
            errorMessage = validationErrors;
          } else {
            errorMessage = error.error?.message || 'Validation failed. Please check your input.';
          }
          errorCode = 'VALIDATION_ERROR';
          break;
        }
        case 429:
          errorMessage = 'Too many requests. Please try again later.';
          errorCode = 'RATE_LIMIT_EXCEEDED';
          break;
        case 500:
          errorMessage = 'Internal server error. Please try again later.';
          errorCode = 'SERVER_ERROR';
          break;
        case 503:
          errorMessage = 'Service unavailable. Please try again later.';
          errorCode = 'SERVICE_UNAVAILABLE';
          break;
      }
    }

    // Log error for debugging
    if (environment.enableConsoleLogs) {
      console.error('API Error:', {
        url: error.url,
        status: error.status,
        statusText: error.statusText,
        error: error.error,
        message: errorMessage,
        code: errorCode
      });
    }

    const apiError: ApiResponse = {
      success: false,
      error: errorMessage,
      status: error.status || 0,
      message: errorMessage,
      Status: false,
    };

    return throwError(() => apiError);
  }

  private setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  private buildCacheKey(method: string, url: string, params?: any): string {
    const paramsStr = params ? JSON.stringify(params) : '';
    return `${method}:${url}:${paramsStr}`;
  }

  private getFromCache(key: string, ttl?: number): any {
    const cached = this.requestCache.get(key);

    if (!cached) return null;

    const now = Date.now();
    const maxAge = ttl || 300000; // Default 5 minutes

    if (now - cached.timestamp > maxAge) {
      this.requestCache.delete(key);
      return null;
    }

    return cached.data;
  }

  private setCache(key: string, data: any, ttl?: number): void {
    this.requestCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || 300000
    });
  }

  private clearExpiredCache(): void {
    const now = Date.now();

    this.requestCache.forEach((value, key) => {
      if (now - value.timestamp > value.ttl) {
        this.requestCache.delete(key);
      }
    });
  }
}
